import uniq from 'lodash-es/uniq';

export const MIME_TYPE_IMAGE_JPEG = 'image/jpeg';
export const MIME_TYPE_IMAGE_GIF = 'image/gif';
export const MIME_TYPE_IMAGE_PNG = 'image/png';
export const MIME_TYPE_IMAGE_SVG_XML = 'image/svg+xml';
export const MIME_TYPE_VIDEO_MP4 = 'video/mp4';
export const MIME_TYPE_VIDEO_QUICKTIME = 'video/quicktime';
export const MIME_TYPE_APPLICATION_JSON = 'application/json';
export const MIME_TYPE_FONT_SNFT = 'application/font-sfnt';
export const MIME_TYPE_FONT_OTF = 'font/otf';
export const MIME_TYPE_FONT_TTF = 'font/ttf';
export const MIME_TYPE_APPLICATION_PDF = 'application/pdf';

export const EXTENSION_JSON = '.json';
export const EXTENSION_TTF = '.ttf';
export const EXTENSION_OTF = '.otf';
export const EXTENSION_PDF = '.pdf';

const MIME_TYPE_BY_EXTENSION = {
  jpg: MIME_TYPE_IMAGE_JPEG,
  jpeg: MIME_TYPE_IMAGE_JPEG,
  gif: MIME_TYPE_IMAGE_GIF,
  png: MIME_TYPE_IMAGE_PNG,
  svg: MIME_TYPE_IMAGE_SVG_XML,
  mp4: MIME_TYPE_VIDEO_MP4,
  mov: MIME_TYPE_VIDEO_QUICKTIME,
  json: MIME_TYPE_APPLICATION_JSON,
  otf: MIME_TYPE_FONT_SNFT,
  ttf: MIME_TYPE_FONT_SNFT,
  pdf: MIME_TYPE_APPLICATION_PDF,
};

const ACCEPTED_TYPES = {
  image: [
    MIME_TYPE_IMAGE_JPEG,
    MIME_TYPE_IMAGE_GIF,
    MIME_TYPE_IMAGE_PNG,
    MIME_TYPE_IMAGE_SVG_XML,
  ],
  video: [MIME_TYPE_VIDEO_MP4, MIME_TYPE_VIDEO_QUICKTIME],
  // json mime-type is not properly detected by all browsers
  lottie: [MIME_TYPE_APPLICATION_JSON, EXTENSION_JSON],
  font: [
    MIME_TYPE_FONT_SNFT,
    MIME_TYPE_FONT_TTF,
    MIME_TYPE_FONT_OTF,
    EXTENSION_TTF,
    EXTENSION_OTF,
  ],
  pdf: [MIME_TYPE_APPLICATION_PDF, EXTENSION_PDF],
};

export const STANDARD_MEDIA_FILE_TYPES = 'image,video,lottie';

export const guessMimeType = file => {
  const mimeType = file.type;
  if (mimeType === '') {
    const fileNameParts = file.name.split('.');
    if (fileNameParts.length === 1) {
      return null;
    }
    const extension = fileNameParts.pop().toLowerCase();
    if (!(extension in MIME_TYPE_BY_EXTENSION)) {
      return null;
    }
    return MIME_TYPE_BY_EXTENSION[extension];
  }
  return mimeType;
};

export const isMimeType = (file, mimeType) => guessMimeType(file) === mimeType;

export const getAcceptedMimeTypesForFileTypes = fileTypes =>
  fileTypes.reduce((all, type) => all.concat(ACCEPTED_TYPES[type] || []), []);

export const getAcceptedMimeTypesForFileTypeString = type =>
  type === null ? [] : getAcceptedMimeTypesForFileTypes(type.split(','));

export const getAcceptedMimeTypesStringForFileTypeString = type =>
  getAcceptedMimeTypesForFileTypeString(type).join(',');

export const isFileAccepted = (file, acceptedTypesString) =>
  getAcceptedMimeTypesStringForFileTypeString(acceptedTypesString).indexOf(
    guessMimeType(file),
  ) > -1;

export const generateFileNotAcceptedMessage = acceptedTypesString => {
  let acceptedExtensions = getAcceptedMimeTypesForFileTypeString(
    acceptedTypesString,
  )
    .map(at => {
      let extension;
      if (at.indexOf('/') !== -1) {
        extension = at.split('/')[1];
        if (extension.indexOf('+') !== -1) {
          extension = extension.substr(0, extension.indexOf('+'));
        }
      } else if (at.indexOf('.') === 0) {
        extension = at.substr(1);
      }
      return extension;
    })
    .filter(Boolean);
  acceptedExtensions = uniq(acceptedExtensions);
  return acceptedExtensions.length === 1
    ? `Only ${acceptedExtensions[0]} files are accepted.`
    : `Only ${acceptedExtensions
        .slice(0, -1)
        .join(', ')} and ${acceptedExtensions.slice(-1)} files are accepted.`;
};

// https://github.com/bubkoo/dataurl-to-blob
export const dataURLtoBlob = dataURL => {
  if (!window || window.window !== window) {
    throw new Error('This module is only available in browser');
  }

  const Blob = window.Blob || window.MozBlob || window.WebKitBlob;
  if (!Blob) {
    throw new Error('Blob was not supported');
  }

  const dataURLPattern = /^data:((.*?)(;charset=.*?)?)(;base64)?,/;

  // parse the dataURL components as per RFC 2397
  const matches = dataURL.match(dataURLPattern);
  if (!matches) {
    throw new Error('invalid dataURI');
  }

  // default to text/plain;charset=utf-8
  const mediaType = matches[2]
    ? matches[1]
    : `text/plain${matches[3] || ';charset=utf-8'}`;

  const isBase64 = !!matches[4];
  const dataString = dataURL.slice(matches[0].length);
  const byteString = isBase64
    ? // convert base64 to raw binary data held in a string
      atob(dataString)
    : // convert base64/URLEncoded data component to raw binary
      decodeURIComponent(dataString);

  const array = [];
  for (let i = 0; i < byteString.length; i += 1) {
    array.push(byteString.charCodeAt(i));
  }

  return new Blob([new Uint8Array(array)], { type: mediaType });
};
